import { useMemo, useState, type ReactNode } from 'react'
import { ChevronDown, ChevronUp, ChevronsUpDown } from 'lucide-react'
import { Checkbox } from './Field'
import { SkeletonRows } from './States'
import { PAGE_SIZE } from '@/lib/constants'
import { cn } from '@/lib/cn'

export interface Column<T> {
  key: string
  header: ReactNode
  width?: string
  align?: 'left' | 'right'
  /** Supplying this makes the header sortable. */
  sortBy?: (row: T) => string | number
  render: (row: T) => ReactNode
}

export interface SortState {
  key: string
  dir: 'asc' | 'desc'
}

interface Props<T> {
  rows: T[]
  columns: Column<T>[]
  rowKey: (row: T) => string
  loading?: boolean
  empty?: ReactNode
  onRowClick?: (row: T) => void
  rowClassName?: (row: T) => string | undefined
  /** Omitting these two turns selection off entirely. */
  selected?: Set<string>
  onSelectedChange?: (next: Set<string>) => void
  defaultSort?: SortState
  pageSize?: number
}

/**
 * Sortable, selectable, sticky-header table with paging.
 *
 * Deliberately not virtualised in Phase 1 (docs/05-implementation-plan.md
 * §3.5) — paging at 50 is cheaper and behaves correctly with find-in-page,
 * which a virtualised grid does not.
 */
export function DataTable<T>({
  rows,
  columns,
  rowKey,
  loading = false,
  empty,
  onRowClick,
  rowClassName,
  selected,
  onSelectedChange,
  defaultSort,
  pageSize = PAGE_SIZE,
}: Props<T>) {
  const [sort, setSort] = useState<SortState | null>(defaultSort ?? null)
  const [page, setPage] = useState(0)

  const selectable = Boolean(selected && onSelectedChange)

  const sorted = useMemo(() => {
    if (!sort) return rows
    const col = columns.find((c) => c.key === sort.key)
    if (!col?.sortBy) return rows
    const { sortBy } = col
    const factor = sort.dir === 'asc' ? 1 : -1
    return [...rows].sort((a, b) => {
      const av = sortBy(a)
      const bv = sortBy(b)
      if (av === bv) return 0
      return (av > bv ? 1 : -1) * factor
    })
  }, [rows, sort, columns])

  const pageCount = Math.max(1, Math.ceil(sorted.length / pageSize))
  const current = Math.min(page, pageCount - 1)
  const visible = sorted.slice(current * pageSize, current * pageSize + pageSize)

  const visibleKeys = visible.map(rowKey)
  const allVisibleSelected =
    selectable && visibleKeys.length > 0 && visibleKeys.every((k) => selected!.has(k))

  function toggleAll() {
    if (!onSelectedChange || !selected) return
    const next = new Set(selected)
    if (allVisibleSelected) visibleKeys.forEach((k) => next.delete(k))
    else visibleKeys.forEach((k) => next.add(k))
    onSelectedChange(next)
  }

  function toggleOne(key: string) {
    if (!onSelectedChange || !selected) return
    const next = new Set(selected)
    if (next.has(key)) next.delete(key)
    else next.add(key)
    onSelectedChange(next)
  }

  function toggleSort(col: Column<T>) {
    if (!col.sortBy) return
    setSort((prev) =>
      prev?.key === col.key
        ? { key: col.key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { key: col.key, dir: 'asc' },
    )
    setPage(0)
  }

  if (loading) return <SkeletonRows cols={Math.min(columns.length, 5)} />
  if (rows.length === 0) return <>{empty ?? null}</>

  return (
    <div>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-left">
          <thead className="sticky top-0 z-10 bg-surface-sunk">
            <tr className="border-y border-rule">
              {selectable && (
                <th scope="col" className="w-9 px-3 py-2">
                  <Checkbox
                    checked={allVisibleSelected}
                    onChange={toggleAll}
                    aria-label={allVisibleSelected ? 'Clear selection' : 'Select all rows'}
                  />
                </th>
              )}
              {columns.map((col) => (
                <th
                  key={col.key}
                  scope="col"
                  style={col.width ? { width: col.width } : undefined}
                  className={cn('px-3 py-2', col.align === 'right' && 'text-right')}
                  aria-sort={
                    sort?.key === col.key
                      ? sort.dir === 'asc'
                        ? 'ascending'
                        : 'descending'
                      : undefined
                  }
                >
                  {col.sortBy ? (
                    <button
                      type="button"
                      onClick={() => toggleSort(col)}
                      className={cn(
                        'field-label inline-flex items-center gap-1 hover:text-ink',
                        col.align === 'right' && 'flex-row-reverse',
                      )}
                    >
                      {col.header}
                      {sort?.key === col.key ? (
                        sort.dir === 'asc' ? (
                          <ChevronUp size={11} aria-hidden />
                        ) : (
                          <ChevronDown size={11} aria-hidden />
                        )
                      ) : (
                        <ChevronsUpDown size={11} className="opacity-40" aria-hidden />
                      )}
                    </button>
                  ) : (
                    <span className="field-label">{col.header}</span>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visible.map((row) => {
              const key = rowKey(row)
              const isSelected = selectable && selected!.has(key)
              return (
                <tr
                  key={key}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  className={cn(
                    'border-b border-rule text-[13px]',
                    onRowClick && 'cursor-pointer',
                    isSelected ? 'bg-brand-blue-50' : onRowClick && 'hover:bg-canvas',
                    rowClassName?.(row),
                  )}
                >
                  {selectable && (
                    <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={isSelected}
                        onChange={() => toggleOne(key)}
                        aria-label={`Select row ${key}`}
                      />
                    </td>
                  )}
                  {columns.map((col) => (
                    <td
                      key={col.key}
                      className={cn('px-3 py-2 align-middle', col.align === 'right' && 'text-right')}
                    >
                      {col.render(row)}
                    </td>
                  ))}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {pageCount > 1 && (
        <div className="flex items-center justify-between px-4 py-2.5 text-[12px] text-ink-muted">
          <span className="tabular">
            {current * pageSize + 1}–{Math.min(sorted.length, (current + 1) * pageSize)} of{' '}
            {sorted.length}
          </span>
          <span className="flex items-center gap-1">
            <button
              type="button"
              disabled={current === 0}
              onClick={() => setPage(current - 1)}
              className="rounded-[4px] px-2 py-1 hover:bg-surface-sunk disabled:opacity-40"
            >
              Previous
            </button>
            <span className="tabular px-1">
              {current + 1} / {pageCount}
            </span>
            <button
              type="button"
              disabled={current >= pageCount - 1}
              onClick={() => setPage(current + 1)}
              className="rounded-[4px] px-2 py-1 hover:bg-surface-sunk disabled:opacity-40"
            >
              Next
            </button>
          </span>
        </div>
      )}
    </div>
  )
}
